import React from "react";
import { logger } from "../../../utils/logger";
import { isChunkLoadError, reloadOnceForChunkError } from "../utils/chunkErrors";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  /** El error es de un bundle viejo pero ya se recargó hace un momento: no insistir. */
  reloadRefused: boolean;
}

const reload = () => window.location.reload();

/**
 * Estilos propios, sin MUI ni theme: este cartel tiene que poder mostrarse
 * incluso cuando lo que falló fue el theme o un provider de la raíz.
 */
const fallbackStyles = `
.gestor-boundary {
  --gestor-boundary-bg: #ffffff;
  --gestor-boundary-fg: #1f2937;
  --gestor-boundary-muted: #6b7280;
  --gestor-boundary-accent: #1976d2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 100dvh;
  padding: 24px;
  box-sizing: border-box;
  text-align: center;
  background: var(--gestor-boundary-bg);
  color: var(--gestor-boundary-fg);
  font-family: Roboto, Helvetica, Arial, sans-serif;
}
.gestor-boundary h1 { margin: 0; font-size: 1.25rem; font-weight: 500; }
.gestor-boundary p { margin: 0; max-width: 360px; font-size: 0.875rem; color: var(--gestor-boundary-muted); }
.gestor-boundary button {
  margin-top: 8px;
  padding: 8px 22px;
  border: 0;
  border-radius: 4px;
  background: var(--gestor-boundary-accent);
  color: #fff;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
}
@media (prefers-color-scheme: dark) {
  .gestor-boundary {
    --gestor-boundary-bg: #121212;
    --gestor-boundary-fg: #e5e7eb;
    --gestor-boundary-muted: #9ca3af;
    --gestor-boundary-accent: #90caf9;
  }
  .gestor-boundary button { color: #0b1a2b; }
}
`;

const AppErrorFallback: React.FC<{ outdated: boolean }> = ({ outdated }) => (
  <div className="gestor-boundary" role="alert">
    <style>{fallbackStyles}</style>
    <h1>{outdated ? "La aplicación se actualizó" : "Algo salió mal"}</h1>
    <p>
      {outdated
        ? "Hay una versión nueva y esta pantalla quedó con la anterior. Recargá para seguir."
        : "La pantalla no se pudo mostrar. Recargá para seguir trabajando; si vuelve a pasar, avisá."}
    </p>
    <button type="button" onClick={reload}>
      Recargar
    </button>
  </div>
);

const UpdatingFallback: React.FC = () => (
  <div className="gestor-boundary">
    <style>{fallbackStyles}</style>
    <p>Actualizando la aplicación...</p>
  </div>
);

/**
 * Último recurso de la interfaz, por fuera de todos los providers: un error en
 * el theme, en el router o en cualquier pantalla desmonta el árbol entero y
 * deja la pantalla en blanco, sin salida en una PWA.
 *
 * Un bundle viejo tras un deploy se resuelve solo (una recarga); cualquier
 * otro error muestra un mensaje con el botón de recargar.
 */
class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, reloadRefused: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isChunkLoadError(error)) {
      logger.warn("Bundle desactualizado tras un deploy; se recarga la app", error.message);
      if (reloadOnceForChunkError()) return;
      this.setState({ reloadRefused: true });
      return;
    }
    logger.error("Error no controlado en la interfaz:", error, info.componentStack);
  }

  render() {
    const { error, reloadRefused } = this.state;
    if (!error) return this.props.children;

    const outdated = isChunkLoadError(error);
    if (outdated && !reloadRefused) return <UpdatingFallback />;
    return <AppErrorFallback outdated={outdated} />;
  }
}

export default AppErrorBoundary;
